import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './RSIConnors.css';
import { getRSI2, getStrategyHealth } from '../api/brapi';
import { getPortfolio, addPosition as apiAddPosition, removePosition as apiRemovePosition } from '../api/portfolio';
import PortfolioModal from './PortfolioModal';
import WebSocketManager from './WebSocketManager';
import DismissibleNotice from './DismissibleNotice';
import EstatisticasHonestas from './EstatisticasHonestas';
import ForaDaAmostra from './ForaDaAmostra';
import EstagioEstrategia from './EstagioEstrategia';
import DadosInsuficientes, { semProntidao } from './DadosInsuficientes';
import { formatTradingDate, formatTradingDateShort } from '../utils/dates';
import { RSI2_STRATEGY_ID, normalizeStrategyId } from '../utils/strategies';
import { formatBacktestPercent, formatProfitFactor, formatStrategyHealthBanner, formatWeightedProfitFactor } from '../utils/backtestRisk';

const ENTRY_RSI     = 10;
const EXIT_RSI      = 70;
const STOP_PCT      = 7;
const POS_SIZE_PCT  = 10;
export const CONNORS_STRATEGY = RSI2_STRATEGY_ID;
const EMPTY_TICKERS = Object.freeze([]);

export function normalizePortfolioStrategies(positions) {
  return (positions || []).map(position => ({
    ...position,
    strategy: normalizeStrategyId(position.strategy),
  }));
}

export function getConnorsOpenPositions(positions) {
  return (positions || []).filter(position =>
    position.status === 'open' && position.strategy === CONNORS_STRATEGY
  );
}

// ── Helpers ───────────────────────────────────────────
function fmtPrice(p) {
  if (p == null) return '—';
  return `R$\u202F${p.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPct(v, sign = true) {
  if (v == null) return '—';
  const s = sign && v > 0 ? '+' : '';
  return `${s}${v.toFixed(1)}%`;
}
function timeAgo(ts) {
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 60)   return `há ${Math.floor(s)}s`;
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  return `há ${Math.floor(s / 3600)}h`;
}
function getRSIClass(rsi2) {
  if (rsi2 <= ENTRY_RSI) return 'entry';
  if (rsi2 >= EXIT_RSI)  return 'high';
  return 'mid';
}
function getStatusInfo(status) {
  switch (status) {
    case 'buy':         return { label: 'COMPRAR',    cls: 'buy',         text: 'Todas as condições atendidas — entrada elegível na abertura.' };
    case 'approaching': return { label: 'PRÓXIMO',    cls: 'approaching', text: 'RSI(2) aproximando da zona de entrada.' };
    case 'watch':       return { label: 'AGUARDAR',   cls: 'watch',       text: 'Acima da MM200 mas sem sinal de entrada ainda.' };
    case 'excluded':    return { label: 'EXCLUÍDO',   cls: 'excluded',    text: 'Abaixo da MM200 — filtro de tendência não atendido.' };
    case 'insufficient': return { label: 'DADOS INSUFICIENTES', cls: 'excluded', text: 'Indicadores ainda sem histórico mínimo.' };
    default:            return { label: '—',           cls: 'watch',       text: '' };
  }
}

function computeConnorsScore(a) {
  const checks = [
    {
      id: 'trend',
      label: 'Acima da MM200 (tendência primária)',
      pass: a.r1AboveMa200,
      value: (a.trendPct >= 0 ? '+' : '') + a.trendPct.toFixed(1) + '%',
      weight: 25,
    },
    {
      id: 'pullback',
      label: 'Preço < MM5 (pullback de curto prazo)',
      pass: a.r2BelowMa5,
      value: a.r2BelowMa5 ? '< MM5' : '> MM5',
      weight: 25,
    },
    {
      id: 'zone',
      label: 'RSI(2) < 30 (zona oversold)',
      pass: a.r3Approaching,
      value: (+a.rsi2).toFixed(1),
      weight: 25,
    },
    {
      id: 'entry',
      label: `RSI(2) < ${ENTRY_RSI} (gatilho de entrada)`,
      pass: a.r4Entry,
      value: (+a.rsi2).toFixed(1),
      weight: 25,
    },
  ];
  let score = 0, passed = 0;
  checks.forEach(c => { if (c.pass) { score += c.weight; passed++; } });
  return { checks, score: Math.round(score), passed, total: checks.length };
}

function ScoreCircle({ score, color }) {
  const r = 46;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - score / 100);
  return (
    <div className="rc-score-circle">
      <svg viewBox="0 0 110 110" width="110" height="110">
        <circle cx="55" cy="55" r={r} fill="none" stroke="#1c2232" strokeWidth="7" />
        <circle
          cx="55" cy="55" r={r}
          fill="none" stroke={color} strokeWidth="7"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 55 55)"
          style={{ transition: 'stroke-dashoffset .55s cubic-bezier(.4,0,.2,1)' }}
        />
      </svg>
      <div className="rc-score-inner">
        <div className="rc-score-num" style={{ color }}>{score}%</div>
        <div className="rc-score-lbl">
          {score >= 100 ? 'Entrada' : score >= 75 ? 'Alta prob.' : score >= 50 ? 'Moderada' : 'Baixa'}
        </div>
      </div>
    </div>
  );
}

// ── Date helper ───────────────────────────────────────
function fmtDateShort(value) {
  return formatTradingDateShort(value);
}
function fmtDateTooltip(value) {
  const formatted = formatTradingDate(value);
  return formatted === '—' ? 'Data indisponível' : formatted;
}

// ── RSI2Chart SVG ─────────────────────────────────────
function RSI2Chart({ asset }) {
  const [tooltip, setTooltip] = useState(null);

  if (!asset) {
    return <div className="rc-chart-empty">Selecione um ativo</div>;
  }
  const ohlc = asset.ohlcHistory || [];
  if (ohlc.length < 5) {
    return <div className="rc-chart-empty">Dados insuficientes</div>;
  }

  // Dimensions
  const W       = 720;
  const PAD_L   = 46;
  const PAD_R   = 14;
  const innerW  = W - PAD_L - PAD_R;

  const H_PRICE  = 168;
  const P_TOP    = 8;
  const P_BOT    = 22;   // space for x-axis labels inside price panel
  const drawHP   = H_PRICE - P_TOP - P_BOT;

  const GAP      = 10;

  const H_RSI    = 82;
  const R_TOP    = 6;
  const R_BOT    = 6;
  const drawHR   = H_RSI - R_TOP - R_BOT;

  const totalH   = H_PRICE + GAP + H_RSI;
  const n        = ohlc.length;

  // X scale — candlestick spacing
  const step   = n > 1 ? innerW / (n - 1) : innerW;
  const barHalf = Math.max(2, step * 0.35);
  const xAt    = i => PAD_L + (n > 1 ? (i / (n - 1)) * innerW : innerW / 2);

  // Price scale (from OHLC highs/lows)
  const allHighs = ohlc.map(d => d.high);
  const allLows  = ohlc.map(d => d.low);
  const pMax = Math.max(...allHighs) * 1.008;
  const pMin = Math.min(...allLows)  * 0.992;
  const pRng = pMax - pMin || 1;
  const pY   = v => P_TOP + ((pMax - v) / pRng) * drawHP;

  // Align MA / RSI series with OHLC (take last n points)
  const rsi2s  = (asset.rsi2History  || []).slice(-n);
  const ma5s   = (asset.ma5History   || []).slice(-n);
  const ma200s = (asset.ma200History || []).slice(-n);

  // MA paths
  const maPath = (series, start) =>
    series.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xAt(start + i).toFixed(1)},${pY(v).toFixed(1)}`).join(' ');

  const off200 = Math.max(0, n - ma200s.length);
  const off5   = Math.max(0, n - ma5s.length);
  const ma200Path = ma200s.length > 1 ? maPath(ma200s, off200) : `M ${PAD_L},${pY(asset.ma200).toFixed(1)} L ${(W - PAD_R)},${pY(asset.ma200).toFixed(1)}`;
  const ma5Path   = ma5s.length   > 1 ? maPath(ma5s,   off5)   : '';

  // RSI panel
  const rsiY0 = H_PRICE + GAP;
  const rY    = v => rsiY0 + R_TOP + ((100 - Math.max(0, Math.min(100, v))) / 100) * drawHR;
  const y70   = rY(70);
  const y50   = rY(50);
  const y30   = rY(30);
  const y10   = rY(10);

  let rsiPath = '';
  if (rsi2s.length > 1) {
    const offR = Math.max(0, n - rsi2s.length);
    rsiPath = rsi2s.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xAt(offR + i).toFixed(1)},${rY(v).toFixed(1)}`).join(' ');
  }

  const lastX   = xAt(n - 1);
  const lastC   = ohlc[n - 1].close;
  const lastPY  = pY(lastC);
  const lastRSI = rsi2s.length ? rsi2s[rsi2s.length - 1] : 50;
  const lastRY  = rY(lastRSI);

  // Y-axis price gridlines (4 levels)
  const pGridLevels = [0, 0.33, 0.67, 1].map(t => pMin + t * pRng);

  // X-axis: show label every ~5 candles + last
  const xLabels = ohlc.map((d, i) => {
    const isLast = i === n - 1;
    if (!isLast && i % 5 !== 0) return null;
    const label = isLast ? 'Hoje' : fmtDateShort(d.date);
    const lx = xAt(i);
    return (
      <text key={i} x={lx} y={H_PRICE - 3} fill={isLast ? '#facc15' : '#4a5568'}
        fontSize="8.5" textAnchor="middle" fontFamily="'JetBrains Mono', monospace">{label}</text>
    );
  });

  // ENTRY label position
  const showEntry = asset.r4Entry;
  const entryX   = lastX;
  const entryY   = lastPY - 18;

  // VENDA signal markers — candles where RSI crosses above 70
  const offR = Math.max(0, n - rsi2s.length);
  const MAX_SIGNAL_MARKERS = 8;
  const vendaSignals = [];
  for (let i = 1; i < rsi2s.length; i++) {
    if (rsi2s[i - 1] < 70 && rsi2s[i] >= 70) {
      const ohlcIdx = offR + i;
      vendaSignals.push({
        x:    xAt(ohlcIdx),
        py:   pY(ohlc[ohlcIdx].high),
        date: ohlc[ohlcIdx].date,
      });
    }
  }
  if (vendaSignals.length > MAX_SIGNAL_MARKERS) vendaSignals.splice(0, vendaSignals.length - MAX_SIGNAL_MARKERS);

  // ENTRADA signal markers — candles where RSI crosses below 10
  const entradaSignals = [];
  for (let i = 1; i < rsi2s.length; i++) {
    if (rsi2s[i - 1] > 10 && rsi2s[i] <= 10) {
      const ohlcIdx = offR + i;
      entradaSignals.push({
        x:    xAt(ohlcIdx),
        py:   pY(ohlc[ohlcIdx].high),
        date: ohlc[ohlcIdx].date,
      });
    }
  }
  if (entradaSignals.length > MAX_SIGNAL_MARKERS) entradaSignals.splice(0, entradaSignals.length - MAX_SIGNAL_MARKERS);

  // De-collision pass — prefer labels above candles, then place them below
  // when the top of the price panel has no more room.
  // Geometry constants shared by the collision logic and the JSX below:
  //   SIG_LABEL_H      — rect height (px)
  //   SIG_LABEL_TOP    — rect y offset above labelY (negative = above)
  //   SIG_LABEL_BOT    — rect y offset below labelY (positive = below)
  //   SIG_LABEL_DY     — default gap between candle price and label centre
  //   SIG_LABEL_PAD    — extra vertical gap between two stacked labels
  //   SIG_VENDA_HW     — half the VENDA rect width  (44 / 2)
  //   SIG_ENTRADA_HW   — half the ENTRADA rect width (56 / 2)
  const SIG_LABEL_H        = 13;
  const SIG_LABEL_TOP      = -10;
  const SIG_LABEL_BOT      = 3;
  const SIG_LABEL_DY       = 18;
  const SIG_LABEL_PAD      = 2;
  const SIG_VENDA_HW       = 22;
  const SIG_ENTRADA_HW     = 28;
  const SIG_ARROW_Y_OFFSET = 14;
  const SIG_DATE_OFFSET    = 9;
  // labelY is the text baseline. Account for the whole label and date, not
  // just its baseline, when keeping annotations inside the price panel.
  const SIG_LABEL_MIN_Y    = P_TOP - SIG_LABEL_TOP;
  const SIG_LABEL_MAX_Y    = H_PRICE - P_BOT - SIG_LABEL_BOT - SIG_DATE_OFFSET;
  const SIG_LABEL_STEP     = SIG_LABEL_H + SIG_LABEL_PAD;
  {
    const placed = [];
    const allSigs = [
      ...vendaSignals.map(s => ({ ...s, hw: SIG_VENDA_HW,   ref: s })),
      ...entradaSignals.map(s => ({ ...s, hw: SIG_ENTRADA_HW, ref: s })),
    ].sort((a, b) => a.x - b.x);

    const overlapsPlacedLabel = (sig, y) => {
      const top = y + SIG_LABEL_TOP;
      const bot = y + SIG_LABEL_BOT;
      return placed.some(p =>
        Math.abs(p.x - sig.x) < (p.hw + sig.hw) &&
        !(bot + SIG_LABEL_PAD < p.top || top - SIG_LABEL_PAD > p.bot)
      );
    };

    const findOpenLabelY = (sig, startY, direction) => {
      for (
        let y = startY;
        y >= SIG_LABEL_MIN_Y && y <= SIG_LABEL_MAX_Y;
        y += direction * SIG_LABEL_STEP
      ) {
        if (!overlapsPlacedLabel(sig, y)) return y;
      }
      return null;
    };

    for (const sig of allSigs) {
      const labelAbove = findOpenLabelY(sig, sig.ref.py - SIG_LABEL_DY, -1);
      const labelBelow = labelAbove == null
        ? findOpenLabelY(sig, sig.ref.py + SIG_LABEL_DY, 1)
        : null;

      // A final clamp keeps every label visible even if the panel is full.
      sig.ref.labelY = labelAbove ?? labelBelow ?? Math.min(
        SIG_LABEL_MAX_Y,
        Math.max(SIG_LABEL_MIN_Y, sig.ref.py - SIG_LABEL_DY)
      );
      sig.ref.labelBelowCandle = labelAbove == null && labelBelow != null;
      placed.push({ x: sig.x, hw: sig.hw, top: sig.ref.labelY + SIG_LABEL_TOP, bot: sig.ref.labelY + SIG_LABEL_BOT });
    }
  }

  return (
    <svg viewBox={`0 0 ${W} ${totalH}`} preserveAspectRatio="xMidYMid meet"
      style={{ display: 'block', width: '100%', height: '100%' }}>

      {/* ── PRICE PANEL ────────────────────────────── */}
      <rect x={PAD_L} y={0} width={innerW} height={H_PRICE - P_BOT} fill="rgba(255,255,255,0.012)" rx="2" />

      {/* Horizontal grid lines */}
      {pGridLevels.map((v, i) => (
        <line key={i} x1={PAD_L} x2={W - PAD_R}
          y1={pY(v).toFixed(1)} y2={pY(v).toFixed(1)}
          stroke="#1e2535" strokeWidth="0.8" />
      ))}

      {/* Y-axis price labels */}
      {pGridLevels.map((v, i) => (
        <text key={i} x={PAD_L - 4} y={(pY(v) + 3).toFixed(1)}
          fill="#3d4860" fontSize="8" textAnchor="end"
          fontFamily="'JetBrains Mono', monospace">
          {v.toFixed(0)}
        </text>
      ))}

      {/* MA200 — purple dashed */}
      {ma200Path && (
        <path d={ma200Path} stroke="#9333ea" strokeWidth="1.4" fill="none"
          strokeDasharray="5 5" opacity="0.8" />
      )}
      {/* MA5 — orange solid */}
      {ma5Path && (
        <path d={ma5Path} stroke="#f97316" strokeWidth="1.6" fill="none" opacity="0.9" />
      )}

      {/* Candlesticks */}
      {ohlc.map((d, i) => {
        const isUp   = d.close >= d.open;
        const color  = isUp ? '#26a69a' : '#ef5350';
        const cx     = xAt(i);
        const bTop   = pY(Math.max(d.open, d.close));
        const bBot   = pY(Math.min(d.open, d.close));
        const bH     = Math.max(1, bBot - bTop);
        return (
          <g key={i}>
            <line x1={cx} x2={cx} y1={pY(d.high).toFixed(1)} y2={pY(d.low).toFixed(1)}
              stroke={color} strokeWidth="0.9" />
            <rect x={(cx - barHalf).toFixed(1)} y={bTop.toFixed(1)}
              width={(barHalf * 2).toFixed(1)} height={bH.toFixed(1)}
              fill={color} />
          </g>
        );
      })}

      {/* MA labels at right edge */}
      {ma200s.length > 0 && (
        <text x={W - PAD_R + 1} y={(pY(ma200s[ma200s.length - 1]) + 3).toFixed(1)}
          fill="#9333ea" fontSize="7.5" fontFamily="'JetBrains Mono', monospace">MM200</text>
      )}
      {ma5s.length > 0 && (
        <text x={W - PAD_R + 1} y={(pY(ma5s[ma5s.length - 1]) + 3).toFixed(1)}
          fill="#f97316" fontSize="7.5" fontFamily="'JetBrains Mono', monospace">MM5</text>
      )}

      {/* ENTRY annotation */}
      {showEntry && (
        <g>
          <line x1={entryX} x2={entryX} y1={entryY + 14} y2={lastPY - 2}
            stroke="#facc15" strokeWidth="1" markerEnd="url(#arrow)" />
          <rect x={entryX - 22} y={entryY - 10} width={44} height={13}
            fill="#facc15" rx="2" />
          <text x={entryX} y={entryY} fill="#0a0f1a" fontSize="8.5" textAnchor="middle"
            fontFamily="'JetBrains Mono', monospace" fontWeight="700">ENTRY</text>
        </g>
      )}

      {/* VENDA annotations — one per RSI crossover above 70 */}
      {vendaSignals.map((sig, idx) => {
        const labelY  = sig.labelY != null ? sig.labelY : sig.py - SIG_LABEL_DY;
        const arrowStartY = sig.labelBelowCandle
          ? labelY - SIG_ARROW_Y_OFFSET
          : labelY + SIG_ARROW_Y_OFFSET;
        const arrowEndY = sig.labelBelowCandle ? sig.py + 2 : sig.py - 2;
        const dateStr = fmtDateShort(sig.date);
        return (
          <g key={idx} style={{ cursor: 'default' }}
            onMouseEnter={() => setTooltip({ x: sig.x, y: labelY + SIG_LABEL_TOP - 2, text: fmtDateTooltip(sig.date) })}
            onMouseLeave={() => setTooltip(null)}>
            <title>VENDA · {dateStr}</title>
            <line x1={sig.x} x2={sig.x} y1={arrowStartY} y2={arrowEndY}
              stroke="#ef5350" strokeWidth="1" markerEnd="url(#arrowVenda)" />
            <rect x={sig.x - SIG_VENDA_HW} y={labelY + SIG_LABEL_TOP} width={SIG_VENDA_HW * 2} height={SIG_LABEL_H}
              fill="#ef5350" rx="2" />
            <text x={sig.x} y={labelY} fill="#fff" fontSize="8.5" textAnchor="middle"
              fontFamily="'JetBrains Mono', monospace" fontWeight="700">VENDA</text>
            {dateStr !== '—' && (
              <text x={sig.x} y={labelY + SIG_LABEL_BOT + 9} fill="#ef5350" fontSize="7"
                textAnchor="middle" fontFamily="'JetBrains Mono', monospace" opacity="0.75">
                {dateStr}
              </text>
            )}
          </g>
        );
      })}

      {/* ENTRADA annotations — one per RSI crossover below 10 */}
      {entradaSignals.map((sig, idx) => {
        const labelY  = sig.labelY != null ? sig.labelY : sig.py - SIG_LABEL_DY;
        const arrowStartY = sig.labelBelowCandle
          ? labelY - SIG_ARROW_Y_OFFSET
          : labelY + SIG_ARROW_Y_OFFSET;
        const arrowEndY = sig.labelBelowCandle ? sig.py + 2 : sig.py - 2;
        const dateStr = fmtDateShort(sig.date);
        return (
          <g key={idx} style={{ cursor: 'default' }}
            onMouseEnter={() => setTooltip({ x: sig.x, y: labelY + SIG_LABEL_TOP - 2, text: fmtDateTooltip(sig.date) })}
            onMouseLeave={() => setTooltip(null)}>
            <title>ENTRADA · {dateStr}</title>
            <line x1={sig.x} x2={sig.x} y1={arrowStartY} y2={arrowEndY}
              stroke="#26a69a" strokeWidth="1" markerEnd="url(#arrowEntrada)" />
            <rect x={sig.x - SIG_ENTRADA_HW} y={labelY + SIG_LABEL_TOP} width={SIG_ENTRADA_HW * 2} height={SIG_LABEL_H}
              fill="#26a69a" rx="2" />
            <text x={sig.x} y={labelY} fill="#fff" fontSize="8.5" textAnchor="middle"
              fontFamily="'JetBrains Mono', monospace" fontWeight="700">ENTRADA</text>
            {dateStr !== '—' && (
              <text x={sig.x} y={labelY + SIG_LABEL_BOT + 9} fill="#26a69a" fontSize="7"
                textAnchor="middle" fontFamily="'JetBrains Mono', monospace" opacity="0.75">
                {dateStr}
              </text>
            )}
          </g>
        );
      })}

      {/* Price panel: chart title */}
      <text x={PAD_L + 5} y={P_TOP + 8} fill="#5e6a85" fontSize="8"
        fontFamily="'JetBrains Mono', monospace" fontWeight="700">
        PREÇO · {asset.ticker}
      </text>

      {/* X-axis date labels */}
      {xLabels}

      {/* X-axis divider */}
      <line x1={PAD_L} x2={W - PAD_R} y1={H_PRICE - P_BOT} y2={H_PRICE - P_BOT}
        stroke="#1e2535" strokeWidth="0.8" />

      {/* ── RSI PANEL ──────────────────────────────── */}
      <rect x={PAD_L} y={rsiY0} width={innerW} height={H_RSI}
        fill="rgba(255,255,255,0.01)" rx="2" />

      {/* RSI label */}
      <text x={PAD_L + 5} y={rsiY0 + R_TOP + 10} fill="#5e6a85" fontSize="8"
        fontFamily="'JetBrains Mono', monospace" fontWeight="700">RSI(2)</text>

      {/* Overbought fill */}
      <rect x={PAD_L} y={rsiY0 + R_TOP} width={innerW} height={Math.max(0, y70 - rsiY0 - R_TOP)}
        fill="rgba(239,83,80,0.05)" />
      {/* VENDA label inside overbought zone */}
      <text
        x={PAD_L + 6}
        y={((rsiY0 + R_TOP) + (y70 - rsiY0 - R_TOP) / 2 + 3).toFixed(1)}
        fill="#ef5350"
        fontSize="7"
        fontFamily="'JetBrains Mono', monospace"
        fontWeight="700"
        opacity="0.55"
        letterSpacing="0.8"
      >VENDA</text>
      {/* Oversold fill (30) */}
      <rect x={PAD_L} y={y30} width={innerW}
        height={Math.max(0, y10 - y30)}
        fill="rgba(38,166,154,0.04)" />
      {/* Entry zone fill (below 10) */}
      <rect x={PAD_L} y={y10} width={innerW}
        height={Math.max(0, rsiY0 + H_RSI - R_BOT - y10)}
        fill="rgba(38,166,154,0.14)" />
      {/* ENTRADA label inside entry zone */}
      <text
        x={PAD_L + 6}
        y={(y10 + (rsiY0 + H_RSI - R_BOT - y10) / 2 + 3).toFixed(1)}
        fill="#26a69a"
        fontSize="7"
        fontFamily="'JetBrains Mono', monospace"
        fontWeight="700"
        opacity="0.55"
        letterSpacing="0.8"
      >ENTRADA</text>

      {/* RSI zone lines */}
      <line x1={PAD_L} x2={W - PAD_R} y1={y70} y2={y70}
        stroke="#ef5350" strokeWidth="0.7" strokeDasharray="3 5" opacity="0.55" />
      <line x1={PAD_L} x2={W - PAD_R} y1={y50} y2={y50}
        stroke="#2a3344" strokeWidth="0.7" strokeDasharray="3 5" />
      <line x1={PAD_L} x2={W - PAD_R} y1={y30} y2={y30}
        stroke="#26a69a" strokeWidth="0.5" strokeDasharray="3 5" opacity="0.35" />
      {/* Entry threshold line at RSI=10 — highlighted */}
      <line x1={PAD_L} x2={W - PAD_R} y1={y10} y2={y10}
        stroke="#26a69a" strokeWidth="1.2" strokeDasharray="4 3" opacity="0.9" />

      {/* RSI y-axis labels */}
      <text x={PAD_L - 4} y={(y70 + 3).toFixed(1)} fill="#ef5350" fontSize="8"
        textAnchor="end" fontFamily="'JetBrains Mono', monospace">70</text>
      <text x={PAD_L - 4} y={(y50 + 3).toFixed(1)} fill="#3d4860" fontSize="8"
        textAnchor="end" fontFamily="'JetBrains Mono', monospace">50</text>
      <text x={PAD_L - 4} y={(y30 + 3).toFixed(1)} fill="#26a69a" fontSize="8"
        textAnchor="end" fontFamily="'JetBrains Mono', monospace" opacity="0.45">30</text>
      <text x={PAD_L - 4} y={(y10 + 3).toFixed(1)} fill="#26a69a" fontSize="8.5"
        textAnchor="end" fontFamily="'JetBrains Mono', monospace" fontWeight="700">10</text>

      {/* RSI line */}
      {rsiPath && <path d={rsiPath} stroke="#a78bfa" strokeWidth="1.8" fill="none" />}

      {/* Today vertical line */}
      <line x1={lastX} x2={lastX} y1={rsiY0} y2={rsiY0 + H_RSI}
        stroke="#facc15" strokeOpacity="0.3" strokeDasharray="2 4" />

      {/* Last RSI dot + value label */}
      {rsiPath && (
        <>
          <circle cx={lastX} cy={lastRY} r="3.5"
            fill={lastRSI <= 10 ? '#26a69a' : lastRSI >= 70 ? '#ef5350' : '#a78bfa'}
            stroke="#111520" strokeWidth="1.5" />
          <rect x={lastX + 5} y={lastRY - 8} width={28} height={12}
            fill="rgba(17,21,32,0.85)" rx="2" />
          <text x={lastX + 19} y={lastRY + 2} fill="#c4b5fd" fontSize="8.5"
            textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontWeight="700">
            {lastRSI.toFixed(1).replace('.', ',')}
          </text>
        </>
      )}

      {/* Arrow marker defs */}
      <defs>
        <marker id="arrow" markerWidth="6" markerHeight="6" refX="3" refY="6"
          orient="auto">
          <path d="M0,0 L3,6 L6,0" fill="none" stroke="#facc15" strokeWidth="1.2" />
        </marker>
        <marker id="arrowVenda" markerWidth="6" markerHeight="6" refX="3" refY="6"
          orient="auto">
          <path d="M0,0 L3,6 L6,0" fill="none" stroke="#ef5350" strokeWidth="1.2" />
        </marker>
        <marker id="arrowEntrada" markerWidth="6" markerHeight="6" refX="3" refY="6"
          orient="auto">
          <path d="M0,0 L3,6 L6,0" fill="none" stroke="#26a69a" strokeWidth="1.2" />
        </marker>
      </defs>

      {/* Hover tooltip */}
      {tooltip && (() => {
        const TW = 72;
        const TH = 14;
        const tx = Math.min(Math.max(tooltip.x - TW / 2, PAD_L), W - PAD_R - TW);
        const ty = tooltip.y - TH - 3;
        return (
          <g style={{ pointerEvents: 'none' }}>
            <rect x={tx} y={ty} width={TW} height={TH}
              fill="#0a0f1a" stroke="#4a5568" strokeWidth="0.7" rx="3" opacity="0.92" />
            <text x={tx + TW / 2} y={ty + TH - 4} fill="#e2e8f0" fontSize="8"
              textAnchor="middle" fontFamily="'JetBrains Mono', monospace">
              {tooltip.text}
            </text>
          </g>
        );
      })()}
    </svg>
  );
}

// ── RSIConnors Main Component ─────────────────────────
function RSIConnors({ onLogout, focusTicker, universeTickers = EMPTY_TICKERS }) {
  const [assets, setAssets]       = useState([]);
  const [backtest, setBacktest]   = useState(null);
  const [strategyHealth, setStrategyHealth] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [selected, setSelected]   = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);

  // WebSocket — live prices
  const handleWsMessage = useCallback((priceData) => {
    setAssets(prev => {
      let changed = false;
      const next = prev.map(a => {
        const upd = priceData.find(p => p.ticker.replace('.SA', '') === a.ticker);
        if (!upd) return a;
        changed = true;
        return { ...a, price: upd.value, change: upd.variation };
      });
      return changed ? next : prev;
    });
  }, []);

  useEffect(() => {
    if (!universeTickers.length) return undefined;
    const wsm = new WebSocketManager(
      (data) => { handleWsMessage(data); },
      () => {}
    );
    wsm.connect();
    const symbols = universeTickers.map(t => `${t}|S`);
    wsm.subscribeToSymbols(symbols);

    return () => {
      wsm.close();
    };
  }, [handleWsMessage, universeTickers]);

  const loadData = useCallback(async () => {
    if (!universeTickers.length) return;
    setLoading(true);
    setError(null);
    try {
      const [resp, health] = await Promise.all([
        getRSI2(universeTickers.join(',')),
        getStrategyHealth().catch(() => null),
      ]);
      const assetList = resp.assets || (Array.isArray(resp) ? resp : []);
      if (assetList.length === 0) throw new Error('Sem dados');
      setAssets(assetList);
      if (resp.backtest) setBacktest(resp.backtest);
      setStrategyHealth(health);
      setLastFetch(Date.now());
      const firstBuy = assetList.find(a => a.status === 'buy') || assetList[0];
      setSelected(firstBuy?.ticker || null);
    } catch (e) {
      console.error('[RSIConnors] loadData error:', e?.response?.status, e?.response?.data, e?.message);
      setError(e?.response?.data?.error || e.message || 'Falha ao carregar dados RSI(2)');
    } finally {
      setLoading(false);
    }
  }, [universeTickers]);

  const loadPortfolio = useCallback(async () => {
    try {
      const data = await getPortfolio();
      setPortfolio(normalizePortfolioStrategies(data.positions));
    } catch (e) {
      console.error('[RSIConnors] portfolio load error:', e);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (focusTicker && assets.some(asset => asset.ticker === focusTicker)) {
      setSelected(focusTicker);
    }
  }, [assets, focusTicker]);

  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('fatcat:data-reload', handler);
    return () => window.removeEventListener('fatcat:data-reload', handler);
  }, [loadData]);

  useEffect(() => {
    loadPortfolio();
    const id = setInterval(loadPortfolio, 30000);
    return () => clearInterval(id);
  }, [loadPortfolio]);

  const handleRemovePosition = useCallback(async (id, e) => {
    e.stopPropagation();
    try {
      await apiRemovePosition(id);
      setPortfolio(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Remove position error:', err);
    }
  }, []);


  const selectedAsset = useMemo(() => assets.find(a => a.ticker === selected) || assets[0], [assets, selected]);

  const counts = useMemo(() => ({
    aboveMa200: assets.filter(a => a.r1AboveMa200).length,
    buy:        assets.filter(a => a.status === 'buy').length,
    approaching:assets.filter(a => a.status === 'approaching').length,
    excluded:   assets.filter(a => a.status === 'excluded').length,
    total:      assets.length,
  }), [assets]);
  const connorsPositions = useMemo(
    () => getConnorsOpenPositions(portfolio),
    [portfolio],
  );
  const selectedHealthKey = selectedAsset?.tier === 'SC' ? 'connorsSc' : 'connorsBlue';
  const riskBacktest = strategyHealth?.strategies?.[selectedHealthKey] || {
    available: false,
    blocked: true,
  };
  const healthUnavailable = riskBacktest.available === false;
  const backtestBlocked = riskBacktest.blocked !== false;
  const shortBacktest = !healthUnavailable && Number(riskBacktest.tradingDays) < 252;
  const entryBlockTitle = healthUnavailable
    ? 'Saúde da estratégia indisponível — entrada bloqueada'
    : riskBacktest.rule?.text || 'Regra canônica de saúde não atendida — entrada bloqueada';
  const backtestPeriod = backtest?.periodStart && backtest?.periodEnd
    ? `${formatTradingDate(backtest.periodStart)}–${formatTradingDate(backtest.periodEnd)}`
    : null;

  // ── Render ────────────────────────────────────────────
  if (loading) {
    return (
      <div className="rc-root">
        <div className="rc-loading">
          <div className="rc-spinner" />
          <span>Carregando RSI(2) Connors — histórico acumulado…</span>
          <span style={{ fontSize: 10, color: '#3d4860' }}>Isso pode levar 10-20 segundos</span>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="rc-root">
        <div className="rc-error-msg">⚠ {error} — <button onClick={loadData} style={{ marginLeft: 8, color: '#5b9dff', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>tentar novamente</button></div>
      </div>
    );
  }

  const sel = selectedAsset;
  const statusInfo = sel ? getStatusInfo(sel.status) : { label: '—', cls: 'watch' };

  const connorsScore = sel ? computeConnorsScore(sel) : null;
  const scoreColor   = connorsScore
    ? (connorsScore.score >= 100 ? '#26a69a' : connorsScore.score >= 75 ? '#facc15' : connorsScore.score >= 50 ? '#f97316' : '#ef5350')
    : '#3d4860';

  const conditions = sel ? [
    {
      num: '01',
      name: 'Filtro de tendência',
      desc: `Preço > MM200`,
      pass: sel.r1AboveMa200,
      value: sel.r1AboveMa200 ? fmtPct(sel.trendPct) : `−${fmtPrice(Math.abs(sel.price - sel.ma200)).replace('R$\u202F','')}`,
      hint: `MM200: ${fmtPrice(sel.ma200)}`,
    },
    {
      num: '02',
      name: 'Pullback de curto prazo',
      desc: `Preço < MM5`,
      pass: sel.r2BelowMa5,
      value: sel.r2BelowMa5 ? '< MM5' : '> MM5',
      hint: `MM5: ${fmtPrice(sel.ma5)}`,
    },
    {
      num: '03',
      name: 'Gatilho de entrada',
      desc: `RSI(2) < ${ENTRY_RSI}`,
      pass: sel.r4Entry,
      warn: sel.r3Approaching && !sel.r4Entry,
      value: sel.rsi2.toFixed(1),
      hint: sel.r4Entry ? 'Sobrevendido extremo' : sel.r3Approaching ? 'Aproximando' : 'Neutro',
    },
    {
      num: '04',
      name: 'Stop loss',
      desc: `−${STOP_PCT}% do preço de entrada`,
      pass: true,
      warn: false,
      value: fmtPrice(sel.stopPrice),
      hint: 'Definido automaticamente',
      isDefined: true,
    },
  ] : [];

  return (
    <div className="rc-root">
      {!healthUnavailable && (
        <DismissibleNotice noticeKey="connors-backtest-risk" eventKey={backtestBlocked ? 'blocked' : 'released'} closeLabel="Fechar aviso de saúde da estratégia Connors" className="rc-backtest-risk-banner" role={backtestBlocked ? 'alert' : 'status'} data-testid="connors-backtest-risk-banner">
          {formatStrategyHealthBanner(riskBacktest)}
        </DismissibleNotice>
      )}
      <div className="rc-backtest-sample-warning" role="note">
        Bloqueio reavaliado diariamente com o backtest ponderado (meia-vida 126 pregões).
      </div>
      {healthUnavailable && (
        <DismissibleNotice noticeKey="connors-health-unavailable" eventKey="unavailable" closeLabel="Fechar aviso de indisponibilidade da estratégia" className="rc-backtest-sample-warning" role="status">
          Saúde da estratégia indisponível — novas entradas permanecem bloqueadas.
        </DismissibleNotice>
      )}
      {shortBacktest && (
        <DismissibleNotice noticeKey="connors-short-sample" eventKey="short-sample" closeLabel="Fechar aviso de amostra curta" className="rc-backtest-sample-warning" role="status" data-testid="connors-backtest-sample-warning">
          Amostra curta: {riskBacktest.tradingDays} pregões
        </DismissibleNotice>
      )}

      {/* KPI Strip */}
      <div className="rc-kpi-strip">
        <div className="rc-kpi">
          <div className="rc-kpi-label">Acima da MM200</div>
          <div className="rc-kpi-value">{counts.aboveMa200}/{counts.total}</div>
        </div>
        <div className="rc-kpi">
          <div className="rc-kpi-label">Setups ativos</div>
          <div className={`rc-kpi-value ${counts.buy > 0 ? 'up' : ''}`}>{counts.buy}</div>
        </div>
        <div className="rc-kpi">
          <div className="rc-kpi-label">Na carteira</div>
          <div className={`rc-kpi-value ${connorsPositions.length > 0 ? 'warn' : ''}`}>
            {connorsPositions.length}
          </div>
        </div>
        <div className="rc-kpi">
          <div className="rc-kpi-label">Expectância ponderada</div>
          <div className={`rc-kpi-value ${backtest?.weightedExpectancyPct >= 0 ? 'up' : 'down'}`}>
            {backtest ? formatBacktestPercent(backtest.weightedExpectancyPct) : '—'}
          </div>
        </div>
        <div className="rc-kpi">
          <div className="rc-kpi-label">PF ponderado</div>
          <div className={`rc-kpi-value ${backtest && (backtest.weightedProfitFactorInfinite || backtest.weightedProfitFactor >= 1) ? 'up' : 'down'}`}>
            {formatWeightedProfitFactor(backtest)}
          </div>
        </div>
        <div className="rc-kpi">
          <div className="rc-kpi-label">Tempo no mercado</div>
          <div className="rc-kpi-value">
            {backtest ? `${backtest.timeInMarketPct.toFixed(1).replace('.',',')}%` : '—'}
          </div>
        </div>
        <div className="rc-kpi">
          <div className="rc-kpi-label">Exposição média</div>
          <div className="rc-kpi-value">
            {backtest ? `${backtest.averageExposurePct.toFixed(1).replace('.',',')}%` : '—'}
          </div>
        </div>
        <div className="rc-kpi-sep" />
        <div className="rc-kpi-capital">
          <div className="rc-kpi-capital-label">Capital em risco</div>
          <div className="rc-kpi-capital-value">{connorsPositions.length * POS_SIZE_PCT}%</div>
        </div>
      </div>

      {/* Body */}
      <div className="rc-body">

        {/* ── LEFT PANEL ── */}
        <div className="rc-left">

          {/* Stock selector + 4 conditions */}
          {sel && (
            <div className="rc-conditions-card">
              <div className="rc-conditions-header">
                <div className="rc-conditions-title">
                  {sel.ticker} — As 4 condições
                </div>
                <span className={`rc-action-btn ${statusInfo.cls}`}>{statusInfo.label}</span>
              </div>

              {/* Stock pills selector */}
              <div className="rc-stock-selector">
                {assets.map(a => (
                  <button
                    key={a.ticker}
                    className={`rc-stock-pill ${selected === a.ticker ? 'active' : ''} ${a.status === 'excluded' ? 'excluded' : ''}`}
                    onClick={() => setSelected(a.ticker)}
                  >
                    {a.ticker}
                  </button>
                ))}
              </div>

              {/* 4 Conditions grid */}
              {semProntidao(sel) && (
                <div className="rc-setup-alert excluded">
                  <div className="rc-alert-text rc-alert-excluded-text">
                    <DadosInsuficientes testId="connors-dados-insuficientes" />
                  </div>
                </div>
              )}
              <div className="rc-conditions-grid" hidden={semProntidao(sel)}>
                {conditions.map((c, i) => {
                  const labelCls = c.isDefined ? 'pass' : c.isExit ? 'fail' : c.pass ? 'pass' : c.warn ? 'warn' : 'fail';
                  const valCls   = c.isDefined ? 'pass' : c.isExit ? 'fail' : c.pass ? 'pass' : c.warn ? 'warn' : 'fail';
                  const icon     = c.isDefined ? '✓' : c.isExit ? '⟳' : c.pass ? '✓' : c.warn ? '~' : '○';
                  const prefix   = c.isDefined ? 'DEFINIDO' : c.isExit ? 'AGUARDANDO' : c.pass ? `OK` : `PENDENTE`;
                  return (
                    <div key={i} className="rc-condition-cell">
                      <div className={`rc-cond-label ${labelCls}`}>
                        <span className="rc-cond-icon">{icon}</span>
                        {prefix}
                      </div>
                      <div className="rc-cond-name">{c.name}</div>
                      <div className={`rc-cond-value ${valCls}`}>{c.value}</div>
                    </div>
                  );
                })}
              </div>

              {/* Setup alert */}
              {sel.status === 'buy' && (
                <>
                  <div className="rc-setup-alert confirmed">
                    <div className="rc-alert-text rc-alert-confirmed-text">
                      <strong>✓ Setup confirmado — entrada elegível na próxima abertura</strong>
                    </div>
                    <button
                      className="rc-add-btn"
                      onClick={() => setShowAddForm(true)}
                      title={backtestBlocked ? 'Exige confirmação de operação por sua conta.' : undefined}
                    >
                      + Carteira
                    </button>
                  </div>
                </>
              )}
              {sel.status === 'approaching' && (
                <div className="rc-setup-alert approaching">
                  <div className="rc-alert-text rc-alert-approaching-text">
                    <strong>~ RSI(2) {sel.rsi2.toFixed(1)} — aguardando cruzar abaixo de {ENTRY_RSI}</strong>
                  </div>
                </div>
              )}
              {sel.status === 'excluded' && (
                <div className="rc-setup-alert excluded">
                  <div className="rc-alert-text rc-alert-excluded-text">
                    <strong>✕ Abaixo da MM200 — filtro de tendência não atendido</strong>
                  </div>
                </div>
              )}
              {sel.status === 'watch' && (
                <div className="rc-setup-alert watch">
                  <div className="rc-alert-text rc-alert-watch-text">
                    <strong>• Monitorando — RSI(2) {sel.rsi2.toFixed(1)} (meta: abaixo de {ENTRY_RSI})</strong>
                  </div>
                </div>
              )}

              {/* Entry / Exit / Stop row */}
              <div className="rc-prices-row">
                <div className="rc-price-cell">
                  <div className="rc-price-cell-label">Entrada</div>
                  <div className="rc-price-cell-value entry">{sel.status === 'buy' ? fmtPrice(sel.price) : '—'}</div>
                </div>
                <div className="rc-price-cell">
                  <div className="rc-price-cell-label">Saída alvo</div>
                  <div className="rc-price-cell-value exit">
                    {sel.status === 'buy' && Number(sel.ma5) < Number(sel.price)
                      ? 'Alvo: fechamento > MM5 (dinâmico)'
                      : sel.status === 'buy' ? fmtPrice(sel.exitTarget) : '—'}
                  </div>
                </div>
                <div className="rc-price-cell">
                  <div className="rc-price-cell-label">Stop loss</div>
                  <div className="rc-price-cell-value stop">{sel.status === 'buy' ? fmtPrice(sel.stopPrice) : '—'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Integrated Chart */}
          {sel && (
            <div className="rc-chart-card">
              <div className="rc-chart-header">
                <div className="rc-chart-title">{sel.ticker} — Preço · MM200 · MM5 · RSI(2)</div>
                <div className="rc-chart-legend">
                  <div className="rc-legend-item">
                    <div className="rc-legend-line" style={{ background: '#26a69a' }} />
                    Preço
                  </div>
                  <div className="rc-legend-item">
                    <div className="rc-legend-line" style={{ background: '#9333ea', borderStyle: 'dashed' }} />
                    MM200
                  </div>
                  <div className="rc-legend-item">
                    <div className="rc-legend-line" style={{ background: '#f97316' }} />
                    MM5
                  </div>
                  <div className="rc-legend-item">
                    <div className="rc-legend-line" style={{ background: '#a78bfa' }} />
                    RSI(2)
                  </div>
                </div>
              </div>
              <div style={{ padding: '0 12px 10px', height: 295 }}>
                <RSI2Chart asset={sel} />
              </div>
            </div>
          )}

          {/* Scanner do mercado */}
          <div className="rc-scanner">
            <div className="rc-scanner-header">
              <div className="rc-scanner-title">Scanner do mercado</div>
              {lastFetch && <div style={{ fontSize: 10, color: '#3d4860' }}>{timeAgo(lastFetch)}</div>}
            </div>
            <table className="rc-table">
              <thead>
                <tr>
                  <th>Ativo</th>
                  <th className="right">Preço</th>
                  <th className="right">Vs MM200</th>
                  <th className="center">RSI(2)</th>
                  <th className="center">Regras</th>
                  <th className="center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {assets.map(a => {
                  const si = getStatusInfo(a.status);
                  return (
                    <tr
                      key={a.ticker}
                      className={selected === a.ticker ? 'selected' : ''}
                      onClick={() => setSelected(a.ticker)}
                    >
                      <td>
                        <div className="rc-ticker-cell">
                          <div className="rc-ticker-sym">{a.ticker}</div>
                          <div className="rc-ticker-name">{semProntidao(a) ? 'Dados insuficientes' : a.status === 'buy' ? 'Setup confirmado' : a.status === 'approaching' ? 'Próximo da entrada' : a.status === 'excluded' ? 'Abaixo da MM200' : 'Aguardando'}</div>
                        </div>
                      </td>
                      <td className="right rc-mono">{fmtPrice(a.price)}</td>
                      <td className={`right rc-change ${a.trendPct >= 0 ? 'up' : 'down'}`}>
                        {semProntidao(a) ? '—' : fmtPct(a.trendPct)}
                      </td>
                      <td className="center">
                        {semProntidao(a)
                          ? '—'
                          : <span className={`rc-rsi-value ${getRSIClass(a.rsi2)}`}>{a.rsi2.toFixed(1)}</span>}
                      </td>
                      <td className="center">
                        <div className="rc-rules-dots" hidden={semProntidao(a)}>
                          <div className={`rc-dot ${a.r1AboveMa200 ? 'pass' : 'fail'}`} title="Preço > MM200" />
                          <div className={`rc-dot ${a.r2BelowMa5 ? 'pass' : 'fail'}`} title="Preço < MM5 (pullback)" />
                          <div className={`rc-dot ${a.r3Approaching ? 'warn' : 'fail'}`} title="RSI(2) < 30" />
                          <div className={`rc-dot ${a.r4Entry ? 'pass' : 'fail'}`} title="RSI(2) < 10" />
                        </div>
                      </td>
                      <td className="center">
                        <button className={`rc-action-btn ${si.cls}`}>{si.label}</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </div>{/* end rc-left */}

        {/* ── RIGHT PANEL ── */}
        <div className="rc-right">

          {/* Score de confluência Connors */}
          {sel && connorsScore && !semProntidao(sel) && (
            <div className="rc-panel">
              <div className="rc-panel-title">
                <span className="rc-panel-title-icon">⚡</span>
                Score de confluência · {sel.ticker}
              </div>
              <div className="rc-score-wrap">
                <div className="rc-score-col">
                  <div className="rc-score-label">Confirmação Connors</div>
                  <ScoreCircle score={connorsScore.score} color={scoreColor} />
                  <div className="rc-score-foot">
                    <strong>{connorsScore.passed}</strong> de <strong>{connorsScore.total}</strong> condições
                  </div>
                </div>
                <div className="rc-score-checklist">
                  {connorsScore.checks.map(c => (
                    <div key={c.id} className={`rc-score-check ${c.pass ? 'pass' : 'fail'}`}>
                      <div className="rc-score-check-icon">{c.pass ? '✓' : '○'}</div>
                      <div className="rc-score-check-label">{c.label}</div>
                      <div className="rc-score-check-value">{c.value}</div>
                    </div>
                  ))}
                </div>
              </div>
              {connorsScore.score >= 100 && (
                <div className="rc-score-confirmed">
                  Todas as condições confirmadas — sinal de entrada válido
                </div>
              )}
              {connorsScore.score < 100 && connorsScore.score >= 75 && (
                <div className="rc-score-pending">
                  {connorsScore.total - connorsScore.passed} condição pendente para confirmar entrada
                </div>
              )}
            </div>
          )}

          {/* Posições abertas — exclusivas da estratégia Connors */}
          <div className="rc-panel">
            <div className="rc-panel-title">
              <span className="rc-panel-title-icon">📂</span>
              Posições abertas · {connorsPositions.length}
            </div>
            {connorsPositions.length === 0 ? (
              <div className="rc-empty-state">Nenhuma posição aberta do RSI(2) Connors.</div>
            ) : connorsPositions.map(pos => {
                const pnl = pos.pnl_pct;
                return (
                  <div
                    key={pos.id}
                    className="rc-position-item"
                    style={{ borderLeft: `3px solid ${pnl == null ? '#2a3344' : pnl >= 0 ? '#26a69a' : '#ef5350'}` }}
                    onClick={() => setSelected(pos.ticker)}
                  >
                    <div className="rc-pos-header">
                      <div className="rc-pos-ticker">{pos.ticker}</div>
                      {pnl != null && (
                        <div className={`rc-pos-change ${pnl >= 0 ? 'up' : 'down'}`}>
                          {pnl >= 0 ? '+' : ''}{pnl.toFixed(1).replace('.', ',')}%
                        </div>
                      )}
                    </div>
                    <div className="rc-pos-row">
                      <span>Entrada</span>
                      <span>{fmtPrice(pos.entry_price)}</span>
                    </div>
                    {pos.current_price && (
                      <div className="rc-pos-row">
                        <span>Atual</span>
                        <span>{fmtPrice(pos.current_price)}</span>
                      </div>
                    )}
                    <div className="rc-pos-row">
                      <span>Qtd</span>
                      <span>{pos.quantity}</span>
                    </div>
                    <button
                      style={{ marginTop: 6, fontSize: 9, background: 'none', border: '1px solid rgba(239,83,80,0.3)', borderRadius: 4, color: '#ef5350', cursor: 'pointer', padding: '2px 6px', width: '100%' }}
                      onClick={(e) => handleRemovePosition(pos.id, e)}
                    >
                      Remover
                    </button>
                  </div>
                );
              })}
          </div>

          {/* Performance */}
          <div className="rc-panel">
            <div className="rc-panel-title">
              <span className="rc-panel-title-icon">📈</span>
              Performance · {backtest ? `Backtest ${backtestPeriod ? `(${backtestPeriod})` : ''} · ${backtest.tradingDays} pregões · ${backtest.tradesTotal} trades` : '—'}
            </div>
            {backtest ? (
              <>
                <div className="rc-perf-grid">
                  <div className="rc-perf-cell">
                    <div className="rc-perf-label">Win rate simples</div>
                    <div className={`rc-perf-value ${backtest.winRate >= 50 ? 'up' : 'down'}`}>
                      {backtest.winRate.toFixed(1).replace('.',',')}%
                    </div>
                  </div>
                  <div className="rc-perf-cell">
                    <div className="rc-perf-label">Expectância simples</div>
                    <div className={`rc-perf-value ${backtest.avgReturnPct >= 0 ? 'up' : 'down'}`}>
                      {backtest.avgReturnPct >= 0 ? '+' : ''}{(+backtest.avgReturnPct).toFixed(2).replace('.',',')}%
                    </div>
                  </div>
                  <div className="rc-perf-cell">
                    <div className="rc-perf-label">Win rate ponderado</div>
                    <div className={`rc-perf-value ${backtest.weightedWinRate >= 50 ? 'up' : 'down'}`}>
                      {Number.isFinite(backtest.weightedWinRate) ? `${backtest.weightedWinRate.toFixed(1).replace('.', ',')}%` : '—'}
                    </div>
                  </div>
                  <div className="rc-perf-cell">
                    <div className="rc-perf-label">Expectância ponderada</div>
                    <div className={`rc-perf-value ${backtest.weightedExpectancyPct >= 0 ? 'up' : 'down'}`}>
                      {formatBacktestPercent(backtest.weightedExpectancyPct)}
                    </div>
                  </div>
                  <div className="rc-perf-cell">
                    <div className="rc-perf-label">PF simples</div>
                    <div className={`rc-perf-value ${backtest.profitFactorInfinite || backtest.profitFactor >= 1 ? 'up' : 'down'}`}>
                      {formatProfitFactor(backtest)}
                    </div>
                  </div>
                  <div className="rc-perf-cell">
                    <div className="rc-perf-label">PF ponderado</div>
                    <div className={`rc-perf-value ${backtest.weightedProfitFactorInfinite || backtest.weightedProfitFactor >= 1 ? 'up' : 'down'}`}>
                      {formatWeightedProfitFactor(backtest)}
                    </div>
                  </div>
                  <div className="rc-perf-cell">
                    <div className="rc-perf-label">Média hold</div>
                    <div className="rc-perf-value">{backtest.avgHold.toFixed(1).replace('.',',')}d</div>
                  </div>
                  <div className="rc-perf-cell">
                    <div className="rc-perf-label">Pior trade</div>
                    <div className="rc-perf-value down">{(+backtest.worstTradePct).toFixed(1).replace('.',',')}%</div>
                  </div>
                  <div className="rc-perf-cell">
                    <div className="rc-perf-label">Trades/ano</div>
                    <div className="rc-perf-value">{backtest.tradesPerYear.toFixed(1).replace('.',',')}</div>
                  </div>
                </div>
                <div style={{ fontSize: 9, color: '#5e6a85', marginBottom: 4 }}>Retorno médio por trade (backtest · B3 real)</div>
                <div className="rc-curve-bar">
                  <div className="rc-curve-fill" style={{
                    width: `${Math.min(100, Math.max(2, 50 + backtest.avgReturnPct * 10))}%`,
                    background: backtest.avgReturnPct >= 0 ? 'linear-gradient(90deg,#26a69a,#34d399)' : 'linear-gradient(90deg,#ef5350,#f87171)',
                  }}>
                    {backtest.avgReturnPct >= 0 ? '+' : ''}{(+backtest.avgReturnPct).toFixed(2).replace('.',',')}%
                  </div>
                </div>
                <EstatisticasHonestas backtest={backtest} testId="connors-estatisticas" />
                <ForaDaAmostra backtest={backtest} testId="connors-fora-da-amostra" />
                <EstagioEstrategia backtest={backtest} testId="connors-estagio" />
              </>
            ) : (
              <div style={{ padding: '20px 0', textAlign: 'center', color: '#3d4860', fontSize: 11 }}>
                Calculando backtest com dados reais...
              </div>
            )}
          </div>

          {/* Rules quick reference */}
          <div className="rc-panel">
            <div className="rc-panel-title">
              <span className="rc-panel-title-icon">⚡</span>
              Regras (referência rápida)
            </div>
            <div className="rc-rules-list">
              <div className="rc-rule-row">
                <span className="rc-rule-key">Universo</span>
                <span className="rc-rule-val highlight">Ibovespa · Grupo</span>
              </div>
              <div className="rc-rule-row">
                <span className="rc-rule-key">Entrada</span>
                <span className="rc-rule-val highlight">RSI(2) &lt; {ENTRY_RSI}</span>
              </div>
              <div className="rc-rule-row">
                <span className="rc-rule-key">Saída A</span>
                <span className="rc-rule-val">Fechamento &gt; MM5</span>
              </div>
              <div className="rc-rule-row">
                <span className="rc-rule-key">Saída B</span>
                <span className="rc-rule-val">RSI(2) &gt; {EXIT_RSI}</span>
              </div>
              <div className="rc-rule-row">
                <span className="rc-rule-key">Saída C</span>
                <span className="rc-rule-val">10 pregões (tempo)</span>
              </div>
              <div className="rc-rule-row">
                <span className="rc-rule-key">Filtro</span>
                <span className="rc-rule-val">Preço &gt; MM200</span>
              </div>
              <div className="rc-rule-row">
                <span className="rc-rule-key">Stop</span>
                <span className="rc-rule-val" style={{ color: '#ef5350' }}>−{STOP_PCT}% do preço</span>
              </div>
              <div className="rc-rule-row">
                <span className="rc-rule-key">Tamanho</span>
                <span className="rc-rule-val">{POS_SIZE_PCT}% por trade</span>
              </div>
            </div>
          </div>


        </div>{/* end rc-right */}
      </div>{/* end rc-body */}

      {/* ── Portfolio Modal ── */}
      {showAddForm && sel && (
        <PortfolioModal
          ticker={sel.ticker}
          name={sel.name || sel.ticker}
          strategy="RSI(2) Connors"
          refs={[
            { label: 'Cotação atual', value: fmtPrice(sel.price) },
            { label: 'RSI(2)',        value: sel.rsi2 != null ? sel.rsi2.toFixed(1) : '—', highlight: true },
            { label: 'MM200',         value: fmtPrice(sel.ma200) },
            { label: 'MM5',           value: fmtPrice(sel.ma5)   },
          ]}
          defaultEntryPrice={sel.price}
          suggestedStop={sel.stopPrice || null}
          suggestedStopLabel={`−${STOP_PCT}% do preço`}
          suggestedTarget={sel.exitTarget || null}
          suggestedTargetLabel="MM5 (saída técnica)"
          requiresOverride={backtestBlocked}
          strategyHealth={riskBacktest}
          onClose={() => setShowAddForm(false)}
          onSave={async (formData) => {
            await apiAddPosition({
              ticker:       sel.ticker,
              name:         sel.name || sel.ticker,
              signal_score: sel.connorsScore ?? null,
              ...formData,
              strategy: CONNORS_STRATEGY,
            });
            setShowAddForm(false);
            await loadPortfolio();
          }}
        />
      )}

    </div>
  );
}

export default RSIConnors;
